import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Loader2, Plus } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { t } from "@/lib/i18n"
import { onWriteError } from "@/lib/writeErrors"

import {
  crearCrecimiento,
  crearOportunidad,
  crearPlanPersonas,
  crearProducto,
} from "@/routes/finance/api"
import { fetchSociedades } from "@/routes/masters/api"

/**
 * DECLARAR DESDE LA PANTALLA.
 *
 * Cesar, punto 7: «debería de poder agregar nuevos clientes a ese revenue build
 * up. Mismos comentarios a costo de ventas, gastos, payroll». La pantalla «De
 * dónde sale» ya enseñaba las capas y la cobertura firmada, y no dejaba escribir
 * ninguna: los endpoints existían y sólo los usaba la siembra. Leer sin poder
 * alimentar obliga a volver a la consola por cada cambio.
 *
 * ===========================================================================
 * EL FORMULARIO VA DONDE ESTÁ LA CIFRA QUE CAMBIA
 * ===========================================================================
 *
 * No hay una pantalla de «mantenimiento de supuestos» aparte. Cada alta cuelga
 * del panel que muestra su efecto: la oportunidad en «Nuevo negocio», el SKU en
 * «Venta por volumen», el gasto que crece en «Lo que va a crecer», la
 * contratación junto a la planilla. Es lo que permite comprobar el número
 * inmediatamente — se declara, se cierra el diálogo y el mes de arriba se mueve.
 *
 * Una pantalla separada obligaría a recordar qué se escribió para ir a buscar
 * dónde se nota, y en un modelo con seis capas eso es exactamente el paso donde
 * nadie comprueba nada.
 *
 * ---------------------------------------------------------------------------
 * CADA ALTA INVALIDA LAS DOS CONSULTAS, Y LA SEGUNDA ES LA QUE SE OLVIDA
 *
 * `forecast-declarado` trae las capas; `forecast-contra-hoja` trae la
 * comparación contra la hoja de gestión. Son dos endpoints y los dos cambian al
 * declarar algo. Invalidando sólo la primera, el panel de arriba se movía y el
 * de «Lo declarado contra tu hoja» seguía mostrando la cobertura anterior — dos
 * cifras de la misma pantalla discrepando, que es el defecto que esta casa
 * persigue.
 */

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
]

/** Las categorías que admite `forecast_crecimiento`, con el nombre que se lee. */
const CATEGORIAS: { codigo: string; nombre: string; ayuda: string }[] = [
  { codigo: "ingreso", nombre: "Ventas", ayuda: "Venta nueva que no viene de un contrato ni de un SKU. El código sigue siendo `ingreso` porque es el valor que guarda la base." },
  { codigo: "costo_ventas", nombre: "Costo de ventas", ayuda: "Lo que cuesta entregar lo que se cobra. Entra en el margen bruto." },
  { codigo: "gasto", nombre: "Gasto operativo", ayuda: "Lo que sostiene la empresa. Por debajo del margen bruto." },
  { codigo: "tecnologia", nombre: "Tecnología", ayuda: "Licencias, nube y herramientas. Va a gasto operativo." },
  { codigo: "otro", nombre: "Otro", ayuda: "Cuando ninguna de las anteriores describe lo que crece." },
]

function useAlta(onHecho?: () => void) {
  const qc = useQueryClient()
  return React.useCallback(() => {
    // LAS DOS, ver el docstring: las capas y la comparación contra la hoja.
    void qc.invalidateQueries({ queryKey: ["forecast-declarado"] })
    void qc.invalidateQueries({ queryKey: ["forecast-contra-hoja"] })
    void qc.invalidateQueries({ queryKey: ["forecast-proyeccion"] })
    onHecho?.()
  }, [qc, onHecho])
}

/** El desplegable de sociedad. Un supuesto sin sociedad no se puede consolidar. */
function CampoSociedad({
  id, valor, onChange,
}: {
  id: string
  valor: string
  onChange: (v: string) => void
}) {
  const q = useQuery({ queryKey: ["sociedades"], queryFn: fetchSociedades })
  const lista = q.data ?? []

  /*
    SE PRESELECCIONA SÓLO SI HAY UNA. Con cuatro sociedades, elegir por el
    usuario significaría colgar un contrato de la sociedad equivocada sin que
    nadie lo decida — y de la sociedad depende el país, la moneda y el
    consolidado.
  */
  React.useEffect(() => {
    if (!valor && lista.length === 1) onChange(lista[0].id)
  }, [lista, valor, onChange])

  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{t("Sociedad")}</Label>
      <Select value={valor} onValueChange={onChange}>
        <SelectTrigger id={id} className="text-xs">
          <SelectValue placeholder={q.isLoading ? t("Cargando…") : t("Elige la sociedad")} />
        </SelectTrigger>
        <SelectContent>
          {lista.map((s) => (
            <SelectItem key={s.id} value={s.id}>
              {s.razon_social} · {s.pais}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

/**
 * «Sin límite» como VALOR PROPIO y no como cadena vacía.
 *
 * Radix rechaza un `SelectItem` con `value=""` —lanza en cuanto se abre el
 * desplegable—, así que «hasta fin de año» necesita un centinela. El resto de la
 * aplicación usa `"todos"` para lo mismo (`JournalPage`, `ReviewQueuePage`); acá
 * el concepto es «no pongas fecha de fin», y se traduce a `null` al enviar.
 */
const SIN_LIMITE = "sin_limite"

/** El mes elegido como número, o `null` si se dejó sin límite. */
function mesNumero(v: string): number | null {
  return !v || v === SIN_LIMITE ? null : Number(v)
}

function CampoMes({
  id, etiqueta, valor, onChange, vacio,
}: {
  id: string
  etiqueta: string
  valor: string
  onChange: (v: string) => void
  vacio?: string
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{etiqueta}</Label>
      <Select value={valor} onValueChange={onChange}>
        <SelectTrigger id={id} className="text-xs">
          <SelectValue placeholder={vacio ?? t("Elige el mes")} />
        </SelectTrigger>
        <SelectContent>
          {vacio ? <SelectItem value={SIN_LIMITE}>{vacio}</SelectItem> : null}
          {MESES.map((m, i) => (
            <SelectItem key={m} value={String(i + 1)}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

/** El envase de los cuatro: mismo botón, mismo diálogo, misma nota al pie. */
function Alta({
  titulo, descripcion, etiqueta, listo, guardando, onGuardar, children, nota,
}: {
  titulo: string
  descripcion: string
  etiqueta: string
  listo: boolean
  guardando: boolean
  onGuardar: () => void
  children: React.ReactNode
  nota?: React.ReactNode
}) {
  const { can } = useRbac()
  const [abierto, setAbierto] = React.useState(false)

  // Sin permiso de edición no se ofrece el botón: un mando que siempre contesta
  // «no puedes» es ruido en una pantalla que ya tiene seis paneles.
  if (!can("edit")) return null

  return (
    <>
      <Button size="xs" variant="outline" onClick={() => setAbierto(true)}>
        <Plus />
        {etiqueta}
      </Button>
      <Dialog
        open={abierto}
        onOpenChange={(v) => {
          setAbierto(v)
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{titulo}</DialogTitle>
            <DialogDescription>{descripcion}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">{children}</div>
          {nota ? (
            <p className="text-2xs leading-5 text-muted-foreground">{nota}</p>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setAbierto(false)}>
              {t("Cancelar")}
            </Button>
            <Button
              size="sm"
              disabled={!listo || guardando}
              onClick={() => {
                onGuardar()
                setAbierto(false)
              }}
            >
              {guardando ? <Loader2 className="animate-spin" /> : null}
              {t("Declararlo")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/* ==========================================================================
   1 · NUEVO NEGOCIO — una oportunidad, con su probabilidad

   LA PROBABILIDAD NO SE PRESUPONE AL 100%. Arranca en 50 y es obligatoria de
   mirar: un forecast que mete cada conversación a valor completo es el que nunca
   avisa de un problema. El ponderado se calcula en el backend y se enseña aquí
   mientras se escribe, para que el número que va a aparecer en el panel no sea
   una sorpresa.
   ========================================================================== */

export function AltaOportunidad({ anio }: { anio: number }) {
  const { toast } = useToast()
  const hecho = useAlta()
  const [entityId, setEntityId] = React.useState("")
  const [cliente, setCliente] = React.useState("")
  const [linea, setLinea] = React.useState("")
  const [monto, setMonto] = React.useState("")
  const [desde, setDesde] = React.useState("")
  const [meses, setMeses] = React.useState("")
  const [prob, setProb] = React.useState("50")
  const [nota, setNota] = React.useState("")

  const m = useMutation({
    mutationFn: () =>
      crearOportunidad({
        entity_id: entityId,
        cliente_nombre: cliente.trim(),
        linea_servicio: linea.trim() || null,
        monto_mensual_usd: Number(monto),
        anio,
        desde_mes: Number(desde),
        meses: meses ? Number(meses) : null,
        probabilidad: Number(prob),
        estado: "prospecto",
        nota: nota.trim() || null,
      }),
    onSuccess: () => {
      toast({
        title: `${cliente.trim()} declarado como nuevo negocio`,
        description: t("Entra al forecast ponderado por su probabilidad, no al valor completo."),
      })
      setCliente(""); setLinea(""); setMonto(""); setDesde(""); setMeses(""); setProb("50"); setNota("")
      hecho()
    },
    onError: onWriteError("la oportunidad", "declarar"),
  })

  const montoN = Number(monto)
  const probN = Number(prob)
  const listo =
    !!entityId && cliente.trim().length >= 2 && montoN > 0 && !!desde &&
    probN >= 0 && probN <= 100

  return (
    <Alta
      titulo={t("Nuevo negocio")}
      descripcion={t("Una oportunidad que todavía no está firmada. Entra al forecast ponderada por su probabilidad.")}
      etiqueta={t("Declarar una oportunidad")}
      listo={listo}
      guardando={m.isPending}
      onGuardar={() => m.mutate()}
      nota={
        listo ? (
          <>
            {t("Al mes entrarían")}{" "}
            <strong className="num-strong">
              {(montoN * probN / 100).toLocaleString("es-PE", { maximumFractionDigits: 0 })}
            </strong>{" "}
            {t("USD ponderados, no")}{" "}
            {montoN.toLocaleString("es-PE", { maximumFractionDigits: 0 })}
            {t(". La diferencia es la probabilidad, y es lo que hace que el forecast avise.")}
          </>
        ) : (
          t("La probabilidad es obligatoria: un forecast que apunta cada conversación al valor completo nunca avisa de un problema.")
        )
      }
    >
      <CampoSociedad id="op-soc" valor={entityId} onChange={setEntityId} />
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="op-cli">{t("Cliente o prospecto")}</Label>
          <Input id="op-cli" value={cliente} onChange={(e) => setCliente(e.target.value)}
                 placeholder={t("Nombre, aunque todavía no sea contraparte")} className="text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="op-lin">{t("Línea de servicio")}</Label>
          <Input id="op-lin" value={linea} onChange={(e) => setLinea(e.target.value)}
                 placeholder={t("Staffing, B2B Lead Gen…")} className="text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="op-monto">{t("Monto mensual (USD)")}</Label>
          <Input id="op-monto" value={monto} inputMode="decimal"
                 onChange={(e) => setMonto(e.target.value)} placeholder="8000" className="num text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="op-prob">{t("Probabilidad (%)")}</Label>
          <Input id="op-prob" value={prob} inputMode="numeric"
                 onChange={(e) => setProb(e.target.value)} className="num text-xs" />
        </div>
        <CampoMes id="op-desde" etiqueta={t("Desde el mes")} valor={desde} onChange={setDesde} />
        <div className="space-y-1">
          <Label htmlFor="op-meses">{t("Cuántos meses")}</Label>
          <Input id="op-meses" value={meses} inputMode="numeric"
                 onChange={(e) => setMeses(e.target.value)}
                 placeholder={t("vacío = hasta fin de año")} className="num text-xs" />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="op-nota">{t("Nota")}</Label>
        <Textarea id="op-nota" value={nota} onChange={(e) => setNota(e.target.value)}
                  rows={2} placeholder={t("De dónde viene, quién la lleva…")} className="text-xs" />
      </div>
    </Alta>
  )
}

/* ==========================================================================
   2 · VENTA POR VOLUMEN — un SKU

   EL COSTO UNITARIO ES LO QUE HACE ÚTIL ESTE FORMULARIO. Sin él sale ingreso sin
   margen, y el consumo masivo es justamente el caso donde el margen unitario
   decide: vender más caja puede empeorar el resultado. Se pide, y si no se da se
   dice que el margen queda sin calcular en vez de suponerlo cero.
   ========================================================================== */

export function AltaProducto({ anio }: { anio: number }) {
  const { toast } = useToast()
  const hecho = useAlta()
  const [entityId, setEntityId] = React.useState("")
  const [producto, setProducto] = React.useState("")
  const [sku, setSku] = React.useState("")
  const [unidades, setUnidades] = React.useState("")
  const [precio, setPrecio] = React.useState("")
  const [costo, setCosto] = React.useState("")
  const [crec, setCrec] = React.useState("0")
  const [desde, setDesde] = React.useState("")
  const [hasta, setHasta] = React.useState("")

  const m = useMutation({
    mutationFn: () =>
      crearProducto({
        entity_id: entityId,
        producto: producto.trim(),
        sku: sku.trim() || null,
        anio,
        desde_mes: Number(desde),
        hasta_mes: mesNumero(hasta),
        unidades_mes: Number(unidades),
        precio_unitario_usd: Number(precio),
        costo_unitario_usd: costo === "" ? null : Number(costo),
        crecimiento_mensual_pct: Number(crec || 0),
      }),
    onSuccess: () => {
      toast({ title: `${producto.trim()} declarado`, description: t("El crecimiento de unidades se compone mes a mes.") })
      setProducto(""); setSku(""); setUnidades(""); setPrecio(""); setCosto(""); setCrec("0"); setDesde(""); setHasta("")
      hecho()
    },
    onError: onWriteError("el producto", "declarar"),
  })

  const u = Number(unidades), p = Number(precio)
  const listo = !!entityId && producto.trim().length >= 2 && u > 0 && p > 0 && !!desde

  return (
    <Alta
      titulo={t("Venta por volumen")}
      descripcion={t("Unidades por mes y precio unitario. No cuelga de un contrato: una empresa de consumo masivo no firma uno por cada caja.")}
      etiqueta={t("Declarar un producto")}
      listo={listo}
      guardando={m.isPending}
      onGuardar={() => m.mutate()}
      nota={
        costo === "" && listo
          ? t("Sin costo unitario calculo el ingreso y dejo el margen sin calcular. No lo supongo cero: en consumo masivo, vender más puede empeorar el resultado y eso hay que poder verlo.")
          : t("El crecimiento es mensual y compuesto: 5% doce veces no es 60%, es 79,6%.")
      }
    >
      <CampoSociedad id="pr-soc" valor={entityId} onChange={setEntityId} />
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="pr-nom">{t("Producto")}</Label>
          <Input id="pr-nom" value={producto} onChange={(e) => setProducto(e.target.value)}
                 className="text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pr-sku">{t("SKU")}</Label>
          <Input id="pr-sku" value={sku} onChange={(e) => setSku(e.target.value)}
                 className="code text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pr-uni">{t("Unidades por mes")}</Label>
          <Input id="pr-uni" value={unidades} inputMode="decimal"
                 onChange={(e) => setUnidades(e.target.value)} className="num text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pr-crec">{t("Crecimiento mensual (%)")}</Label>
          <Input id="pr-crec" value={crec} inputMode="decimal"
                 onChange={(e) => setCrec(e.target.value)} className="num text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pr-pre">{t("Precio unitario (USD)")}</Label>
          <Input id="pr-pre" value={precio} inputMode="decimal"
                 onChange={(e) => setPrecio(e.target.value)} className="num text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pr-cos">{t("Costo unitario (USD)")}</Label>
          <Input id="pr-cos" value={costo} inputMode="decimal"
                 onChange={(e) => setCosto(e.target.value)}
                 placeholder={t("para que haya margen")} className="num text-xs" />
        </div>
        <CampoMes id="pr-desde" etiqueta={t("Desde el mes")} valor={desde} onChange={setDesde} />
        <CampoMes id="pr-hasta" etiqueta={t("Hasta el mes")} valor={hasta} onChange={setHasta}
                  vacio={t("hasta fin de año")} />
      </div>
    </Alta>
  )
}

/* ==========================================================================
   3 · LO QUE VA A CRECER — gasto, costo o tecnología, por mes o por trimestre

   VA POR PAÍS Y NO POR SOCIEDAD, porque así lo guarda `forecast_crecimiento`: lo
   que crece se declara al nivel en que se decide («vamos a gastar 3.000 al mes
   más en nube en Perú»), y el consolidado lo reparte. La categoría decide a qué
   cuenta del P&L entra, y de eso depende el margen bruto — por eso se elige de
   una lista y no se escribe.
   ========================================================================== */

export function AltaCrecimiento({ anio }: { anio: number }) {
  const { toast } = useToast()
  const hecho = useAlta()
  /*
    LOS PAÍSES SALEN DE LAS SOCIEDADES, no de una lista fija ni de lo ya
    declarado. De una lista fija porque el día que entre un país nuevo nadie se
    acordaría de añadirlo aquí; y no de lo ya declarado porque entonces el primer
    crecimiento de un país no se podría declarar nunca — el mismo círculo que
    dejaba los paneles sin botón.
  */
  const soc = useQuery({ queryKey: ["sociedades"], queryFn: fetchSociedades })
  const paises = React.useMemo(
    () => [...new Set((soc.data ?? []).map((x) => x.pais))].sort(),
    [soc.data],
  )
  const [pais, setPais] = React.useState("")
  React.useEffect(() => {
    if (!pais && paises.length === 1) setPais(paises[0])
  }, [paises, pais])
  const [categoria, setCategoria] = React.useState("gasto")
  const [concepto, setConcepto] = React.useState("")
  const [importe, setImporte] = React.useState("")
  const [desde, setDesde] = React.useState("")
  const [hasta, setHasta] = React.useState("")
  const [nota, setNota] = React.useState("")

  const m = useMutation({
    mutationFn: () =>
      crearCrecimiento({
        pais,
        anio,
        desde_mes: Number(desde),
        hasta_mes: mesNumero(hasta),
        categoria,
        concepto: concepto.trim(),
        importe_mensual_usd: Number(importe),
        nota: nota.trim() || null,
      }),
    onSuccess: () => {
      toast({ title: `${concepto.trim()} declarado`, description: `${pais} · ${categoria}` })
      setConcepto(""); setImporte(""); setDesde(""); setHasta(""); setNota("")
      hecho()
    },
    onError: onWriteError("el crecimiento", "declarar"),
  })

  const cat = CATEGORIAS.find((c) => c.codigo === categoria) ?? null
  const listo = !!pais && concepto.trim().length >= 2 && Number(importe) > 0 && !!desde

  return (
    <Alta
      titulo={t("Lo que va a crecer")}
      descripcion={t("Un costo, un gasto o un ingreso que todavía no está en ningún maestro. Por mes, o por trimestre con desde y hasta.")}
      etiqueta={t("Declarar un crecimiento")}
      listo={listo}
      guardando={m.isPending}
      onGuardar={() => m.mutate()}
      nota={cat?.ayuda}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="cr-pais">{t("País")}</Label>
          <Select value={pais} onValueChange={setPais}>
            <SelectTrigger id="cr-pais" className="text-xs">
              <SelectValue placeholder={t("Elige el país")} />
            </SelectTrigger>
            <SelectContent>
              {paises.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="cr-cat">{t("Qué es")}</Label>
          <Select value={categoria} onValueChange={setCategoria}>
            <SelectTrigger id="cr-cat" className="text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIAS.map((c) => (
                <SelectItem key={c.codigo} value={c.codigo}>{c.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="cr-con">{t("Concepto")}</Label>
          <Input id="cr-con" value={concepto} onChange={(e) => setConcepto(e.target.value)}
                 placeholder={t("Nube y licencias, oficina nueva…")} className="text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="cr-imp">{t("Importe mensual (USD)")}</Label>
          <Input id="cr-imp" value={importe} inputMode="decimal"
                 onChange={(e) => setImporte(e.target.value)} className="num text-xs" />
        </div>
        <div />
        <CampoMes id="cr-desde" etiqueta={t("Desde el mes")} valor={desde} onChange={setDesde} />
        <CampoMes id="cr-hasta" etiqueta={t("Hasta el mes")} valor={hasta} onChange={setHasta}
                  vacio={t("de ahí en adelante")} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="cr-nota">{t("Nota")}</Label>
        <Textarea id="cr-nota" value={nota} onChange={(e) => setNota(e.target.value)}
                  rows={2} className="text-xs" />
      </div>
    </Alta>
  )
}

/* ==========================================================================
   4 · LA GENTE QUE VIENE — una contratación o un aumento

   `es_facturable` NO ES UN DETALLE DE RECURSOS HUMANOS: decide si esa persona
   va a COSTO DE VENTAS o a GASTO OPERATIVO, y de ese corte depende el margen
   bruto. Es el mismo criterio que usa el P&L real (`finance/router.py`), para
   que comparar forecast y real mida el negocio y no el criterio.

   El AUMENTO va en la misma tabla y es otra pregunta: un porcentaje sobre la
   planilla existente, desde un mes. Se ofrecen los dos en un solo formulario
   porque son la misma decisión vista dos veces —cuánto va a costar la gente— y
   separarlos en dos botones haría buscar cuál es cuál.
   ========================================================================== */

export function AltaPlanPersonas({ anio }: { anio: number }) {
  const { toast } = useToast()
  const hecho = useAlta()
  const [entityId, setEntityId] = React.useState("")
  const [tipo, setTipo] = React.useState<"contratacion" | "aumento">("contratacion")
  const [puesto, setPuesto] = React.useState("")
  const [cantidad, setCantidad] = React.useState("1")
  const [costo, setCosto] = React.useState("")
  const [facturable, setFacturable] = React.useState("si")
  const [pct, setPct] = React.useState("")
  const [desde, setDesde] = React.useState("")
  const [hasta, setHasta] = React.useState("")

  const m = useMutation({
    mutationFn: () =>
      crearPlanPersonas({
        entity_id: entityId,
        tipo,
        anio,
        desde_mes: Number(desde),
        hasta_mes: mesNumero(hasta),
        puesto: tipo === "contratacion" ? puesto.trim() || null : null,
        cantidad: tipo === "contratacion" ? Number(cantidad || 1) : null,
        costo_mensual_usd: tipo === "contratacion" ? Number(costo) : null,
        es_facturable: tipo === "contratacion" ? facturable === "si" : null,
        porcentaje: tipo === "aumento" ? Number(pct) : null,
      }),
    onSuccess: () => {
      toast({
        title: tipo === "contratacion" ? `${puesto.trim() || "Contratación"} declarada` : t("Aumento declarado"),
        description: tipo === "contratacion"
          ? (facturable === "si"
            ? t("Va a costo de ventas: entrega trabajo que se cobra.")
            : t("Va a gasto operativo: sostiene la empresa."))
          : t("Se aplica sobre la planilla existente, y engrosa."),
      })
      setPuesto(""); setCantidad("1"); setCosto(""); setPct(""); setDesde(""); setHasta("")
      hecho()
    },
    onError: onWriteError("el plan de personas", "declarar"),
  })

  const listo = !!entityId && !!desde && (
    tipo === "contratacion"
      ? puesto.trim().length >= 2 && Number(costo) > 0
      : Number(pct) !== 0 && pct !== ""
  )

  return (
    <Alta
      titulo={t("La gente que viene")}
      descripcion={t("Una contratación prevista, o un aumento sobre la planilla que ya tienes.")}
      etiqueta={t("Declarar gente o un aumento")}
      listo={listo}
      guardando={m.isPending}
      onGuardar={() => m.mutate()}
      nota={
        tipo === "contratacion"
          ? t("Si entrega trabajo que se cobra va a costo de ventas; si sostiene la empresa, a gasto operativo. De ese corte depende tu margen bruto, y es el mismo que usa el P&L real.")
          : t("El aumento se aplica sobre la planilla existente y engrosa el costo del mes en adelante, no sólo el mes que se declara.")
      }
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="pp-tipo">{t("Qué quieres declarar")}</Label>
          <Select value={tipo} onValueChange={(v) => setTipo(v as "contratacion" | "aumento")}>
            <SelectTrigger id="pp-tipo" className="text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="contratacion">{t("Una contratación")}</SelectItem>
              <SelectItem value="aumento">{t("Un aumento de sueldos")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <CampoSociedad id="pp-soc" valor={entityId} onChange={setEntityId} />
      </div>

      {tipo === "contratacion" ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="pp-puesto">{t("Puesto")}</Label>
            <Input id="pp-puesto" value={puesto} onChange={(e) => setPuesto(e.target.value)}
                   placeholder={t("Diseñador senior")} className="text-xs" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pp-cant">{t("Cuántas personas")}</Label>
            <Input id="pp-cant" value={cantidad} inputMode="numeric"
                   onChange={(e) => setCantidad(e.target.value)} className="num text-xs" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pp-costo">{t("Costo mensual por persona (USD)")}</Label>
            <Input id="pp-costo" value={costo} inputMode="decimal"
                   onChange={(e) => setCosto(e.target.value)} className="num text-xs" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pp-fact">{t("¿Su trabajo se cobra?")}</Label>
            <Select value={facturable} onValueChange={setFacturable}>
              <SelectTrigger id="pp-fact" className="text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="si">{t("Sí · va a costo de ventas")}</SelectItem>
                <SelectItem value="no">{t("No · va a gasto operativo")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <Label htmlFor="pp-pct">{t("Porcentaje de aumento")}</Label>
          <Input id="pp-pct" value={pct} inputMode="decimal"
                 onChange={(e) => setPct(e.target.value)} placeholder="8" className="num text-xs" />
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        <CampoMes id="pp-desde" etiqueta={t("Desde el mes")} valor={desde} onChange={setDesde} />
        <CampoMes id="pp-hasta" etiqueta={t("Hasta el mes")} valor={hasta} onChange={setHasta}
                  vacio={t("de ahí en adelante")} />
      </div>
    </Alta>
  )
}
